import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  getGetResourceObjectQueryKey,
  getGetResourceQueryKey,
  createResourceObjectBorrow,
  updateResourceObjectBorrow,
} from "@/orval/gen"
import { useTranslation } from "react-i18next"
import { LendFormData } from "../types"

interface UseBorrowLogicProps {
  resourceObjectId: number
  resourceObject: any
}

export function useBorrowLogic({
  resourceObjectId,
  resourceObject,
}: UseBorrowLogicProps) {
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()

  const [isReturnModalOpen, setIsReturnModalOpen] = React.useState(false)
  const [isLendModalOpen, setIsLendModalOpen] = React.useState(false)
  const [returnEndDate, setReturnEndDate] = React.useState<Date | null>(null)
  const [lendFormData, setLendFormData] = React.useState<LendFormData>({
    startDate: new Date(),
    endDate: null,
    expectedEndDate: null,
    personId: null,
    locationId: null,
    note: "",
    rentalFee: null,
    cleaningCost: null,
  })

  const resource = resourceObject?.resourceId ? resourceObject : undefined

  React.useEffect(() => {
    if (isLendModalOpen) {
      setLendFormData({
        startDate: new Date(),
        endDate: null,
        expectedEndDate: null,
        personId: null,
        locationId: null,
        note: "",
        rentalFee: resource?.rentalFee ?? null,
        cleaningCost: resource?.cleaningCost ?? null,
      })
    }
  }, [isLendModalOpen, resource])

  React.useEffect(() => {
    if (isReturnModalOpen) {
      setReturnEndDate(new Date())
    }
  }, [isReturnModalOpen])

  const createBorrowMutation = useMutation({
    mutationFn: async (data: LendFormData) => {
      if (!data.personId && !data.locationId) {
        throw new Error(t("RequirePersonOrLocation"))
      }

      const formatDate = (date: Date | null | undefined): string => {
        return date
          ? new Date(date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0]
      }

      const payload = {
        resourceObjectId,
        startDate: formatDate(data.startDate),
        endDate: data.endDate
          ? new Date(data.endDate).toISOString().split("T")[0]
          : undefined,
        expectedEndDate: data.expectedEndDate
          ? new Date(data.expectedEndDate).toISOString().split("T")[0]
          : undefined,
        personId: data.personId ? parseInt(data.personId, 10) : null,
        locationId: data.locationId ? parseInt(data.locationId, 10) : null,
        note: data.note || null,
        rentalFee: data.rentalFee,
        cleaningCost: data.cleaningCost,
      }

      return createResourceObjectBorrow(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetResourceObjectQueryKey({ resourceObjectId }),
      })
      if (resourceObject?.resourceId) {
        queryClient.invalidateQueries({
          queryKey: getGetResourceQueryKey({
            resourceId: resourceObject.resourceId,
          }),
        })
      }
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectInsurances", resourceObjectId],
      })
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectMonetaryValues", resourceObjectId],
      })
      setIsLendModalOpen(false)
      setLendFormData({
        startDate: new Date(),
        endDate: null,
        expectedEndDate: null,
        personId: null,
        locationId: null,
        note: "",
        rentalFee: null,
        cleaningCost: null,
      })
    },
  })

  const updateBorrowMutation = useMutation({
    mutationFn: async () => {
      if (!activeBorrow) {
        throw new Error("No active borrow found")
      }

      const payload = {
        resourceObjectBorrowId: activeBorrow.resourceObjectBorrowId,
        endDate: (returnEndDate ?? new Date()).toISOString().split("T")[0],
      }

      return updateResourceObjectBorrow(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getGetResourceObjectQueryKey({ resourceObjectId }),
      })
      if (resourceObject?.resourceId) {
        queryClient.invalidateQueries({
          queryKey: getGetResourceQueryKey({
            resourceId: resourceObject.resourceId,
          }),
        })
      }
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectInsurances", resourceObjectId],
      })
      queryClient.invalidateQueries({
        queryKey: ["resourceObjectMonetaryValues", resourceObjectId],
      })
      setIsReturnModalOpen(false)
      setReturnEndDate(null)
    },
  })

  const activeBorrow = React.useMemo(() => {
    const borrows = resourceObject?.resourceObjectBorrows
    if (!borrows?.length) return undefined

    const nowMs = Date.now()

    return borrows.find((b: any) => {
      if (!b.startDate) return false
      const startMs = new Date(b.startDate).getTime()
      const endMs = b.endDate ? new Date(b.endDate).getTime() : Infinity

      return startMs <= nowMs && endMs > nowMs
    })
  }, [resourceObject])

  const pastBorrows = React.useMemo(() => {
    const borrows = resourceObject?.resourceObjectBorrows
    if (!borrows?.length) return []

    const nowMs = Date.now()

    return borrows
      .map((b: any) => ({
        ...b,
        endMs: b.endDate ? new Date(b.endDate).getTime() : null,
      }))
      .filter(
        (b: any): b is typeof b & { endMs: number } =>
          b.endMs !== null && b.endMs < nowMs
      )
      .sort((a: any, b: any) => b.endMs - a.endMs)
      .map(({ endMs, ...b }: any) => b)
  }, [resourceObject])

  const hasPersonOrLocation =
    !!lendFormData.personId || !!lendFormData.locationId
  const personIsRequired = !lendFormData.locationId
  const locationIsRequired = !lendFormData.personId
  const formValidationError = createBorrowMutation.error
    ? (createBorrowMutation.error as Error).message
    : null

  return {
    isReturnModalOpen,
    setIsReturnModalOpen,
    isLendModalOpen,
    setIsLendModalOpen,
    returnEndDate,
    setReturnEndDate,
    lendFormData,
    setLendFormData,
    createBorrowMutation,
    updateBorrowMutation,
    activeBorrow,
    pastBorrows,
    hasPersonOrLocation,
    personIsRequired,
    locationIsRequired,
    formValidationError: createBorrowMutation.error
      ? (createBorrowMutation.error as Error).message
      : null,
  }
}
